import { css } from '@emotion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSetAtom } from 'jotai';
import {
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  PaginationChangedEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { AgTableWithBaseStyle } from '../../components/AgTable/AgTable2';
import { multiRowSelectionDefs } from '../../components/AgTable/rowSelectionDefs';
import { Loader } from '../../components/Loader/Loader';
import { agForesightCompactTheme } from '../../theme/ag-foresight';
import { colors } from '../../theme/colors';
import { COMPACT_GRID_ROW_HEIGHT, gridAutoHeightCss } from '../../theme/gridStyle';
import { ISourcingCompanyRow, ISourcingPersonRow } from '../../schemas/SourcingListItems.schema';
import { SourcingDetailPanel } from './components/SourcingDetailPanel';
import { SourcingGridActionBar } from './components/SourcingGridActionBar';
import {
  PAGE_SIZE_OPTIONS,
  resolveInitialPageSize,
  SourcingGridPaginationFooter,
} from './components/SourcingGridPaginationFooter';
import { useSourcingColDefs } from './hooks/useSourcingColDefs';
import { sourcingGridApiAtom } from './state/SourcingListsState';

interface PaginationState {
  readonly currentPage: number;
  readonly totalPages: number;
  readonly totalRowCount: number;
  readonly pageSize: number;
}

function readPaginationState(api: GridApi, fallbackPageSize: number): PaginationState {
  return {
    currentPage: api.paginationGetCurrentPage(),
    totalPages: api.paginationGetTotalPages(),
    totalRowCount: api.paginationGetRowCount(),
    pageSize: api.paginationGetPageSize() ?? fallbackPageSize,
  };
}

const gridContainerCss = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

// When the custom pagination footer renders as a sibling of the grid wrapper,
// force the grid wrapper to flex-grow so the footer claims its own row.
const gridFillRemainingCss = css`
  flex: 1 1 0;
  min-height: 0;
  display: flex;
`;

// When SourcingGridActionBar sits directly above the grid, its bottom border
// visually replaces the grid's top border — so we suppress the duplicate.
const noTopBorderCss = css`
  .ag-root-wrapper {
    border-top: none;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
`;

const sourcingGridCss = css`
  width: 100%;

  .ag-overlay-no-rows-center {
    color: ${colors.text.secondary};
    font-size: 14px;
    line-height: 1.43;
    letter-spacing: 0.17px;
  }

  .ag-row:not(.ag-full-width-row) {
    background-color: var(--ag-background-color);
  }

  .ag-details-row {
    padding: 0;
  }

  .ag-row.ag-full-width-row {
    background: white;
    border-bottom: none;
  }

  .ag-row.ag-full-width-row:hover {
    background: white;
  }

  .ag-row:has(+ .ag-full-width-row) {
    background: ${colors.primary[5]};
  }
`;

const defaultColDef = {
  sortable: true,
  resizable: true,
  filter: true,
};

function isRowMasterCompany(row: ISourcingCompanyRow): boolean {
  const hasNews = row.recentNews != null && row.recentNews.length > 0;
  const hasInvestors = row.allInvestors != null && row.allInvestors.length > 0;
  const hasFunding = row.totalFunding != null || row.lastFundingRaised != null || row.postValuation != null;
  const hasOverview =
    row.shortDescription != null || row.ceoName != null || row.founders != null || row.website != null;
  return hasNews || hasInvestors || hasFunding || hasOverview;
}

interface SourcingGridProps {
  readonly entityType: 'company' | 'person';
  readonly listType?: 'static' | 'active';
  readonly selectedCount?: number;
  readonly rowData: ISourcingCompanyRow[] | ISourcingPersonRow[];
  readonly isLoading?: boolean;
  readonly pagination?: true;
  readonly paginationPageSize?: number;
  readonly onSelectionChanged?: (count: number) => void;
  readonly onDeleteSelected?: () => void;
  readonly onAddToList?: () => void;
}

export function SourcingGrid({
  entityType,
  listType,
  selectedCount = 0,
  rowData,
  isLoading = false,
  pagination,
  paginationPageSize,
  onSelectionChanged,
  onDeleteSelected,
  onAddToList,
}: SourcingGridProps) {
  const setGridApi = useSetAtom(sourcingGridApiAtom);
  const colDefs = useSourcingColDefs();

  // Imperative handle to the grid for the pagination footer callbacks.
  // Held in a ref to avoid extra re-renders on each grid event.
  const gridApiRef = useRef<GridApi | null>(null);

  const initialPageSize = resolveInitialPageSize(paginationPageSize);
  const [paginationState, setPaginationState] = useState<PaginationState>({
    currentPage: 0,
    totalPages: 0,
    totalRowCount: 0,
    pageSize: initialPageSize,
  });

  const isRowMaster = useCallback(
    (row: ISourcingCompanyRow | ISourcingPersonRow) => {
      if (entityType === 'company') {
        return isRowMasterCompany(row as ISourcingCompanyRow);
      }

      return false;
    },
    [entityType]
  );

  const getRowId = useCallback((params: GetRowIdParams) => {
    return params.data.id;
  }, []);

  useEffect(
    function resetGridApiOnUnmount() {
      return () => setGridApi(null);
    },
    [setGridApi]
  );

  const handleGridReady = useCallback(
    (event: GridReadyEvent) => {
      gridApiRef.current = event.api;
      setGridApi(event.api);
    },
    [setGridApi]
  );

  const handlePaginationChanged = useCallback(
    (event: PaginationChangedEvent) => {
      // gridApiRef is already populated in onGridReady; no need to re-assign here.
      setPaginationState(readPaginationState(event.api, initialPageSize));
    },
    [initialPageSize]
  );

  const handlePageChange = useCallback((page: number) => {
    gridApiRef.current?.paginationGoToPage(page);
  }, []);

  const handlePageSizeChange = useCallback((nextPageSize: number) => {
    const api = gridApiRef.current;
    if (api == null) {
      return;
    }
    // AG Grid resets to page 0 automatically when paginationPageSize changes;
    // we still call paginationGoToPage(0) defensively to guarantee the spec.
    api.setGridOption('paginationPageSize', nextPageSize);
    api.paginationGoToPage(0);
  }, []);

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent) => {
      onSelectionChanged?.(event.api.getSelectedNodes().length);
    },
    [onSelectionChanged]
  );

  if (isLoading) {
    return <Loader height='100%' />;
  }

  const useFlexLayout = pagination || rowData.length === 0;

  const gridElement = (
    <AgTableWithBaseStyle
      theme={agForesightCompactTheme}
      domLayout={useFlexLayout ? undefined : 'autoHeight'}
      rowHeight={COMPACT_GRID_ROW_HEIGHT}
      headerHeight={24}
      columnDefs={colDefs[entityType]}
      defaultColDef={defaultColDef}
      rowSelection={multiRowSelectionDefs}
      selectionColumnDef={{
        pinned: 'left',
        maxWidth: 50,
        cellStyle: ({ node }) => ({
          boxShadow: node.master && node.expanded ? `inset 3px 0 0 ${colors.primary[60]}` : 'none',
        }),
      }}
      onRowGroupOpened={({ api, node }) => api.refreshCells({ rowNodes: [node], force: true })}
      masterDetail
      detailRowAutoHeight
      detailCellRenderer={SourcingDetailPanel}
      rowData={rowData}
      isRowMaster={isRowMaster}
      getRowId={getRowId}
      popupParent={document.body}
      pagination={pagination}
      paginationPageSize={pagination ? initialPageSize : undefined}
      // Hide AG Grid's built-in panel; the custom footer below replaces it.
      // `pagination` is typed as `true | undefined`, so it's already a valid bool prop.
      suppressPaginationPanel={pagination}
      onGridReady={handleGridReady}
      onPaginationChanged={pagination ? handlePaginationChanged : undefined}
      onSelectionChanged={handleSelectionChanged}
    />
  );

  return (
    <>
      {listType && (
        <SourcingGridActionBar
          selectedCount={selectedCount}
          entityType={entityType}
          listType={listType}
          onDelete={onDeleteSelected}
          onAddToList={onAddToList}
        />
      )}
      <div
        css={[
          sourcingGridCss,
          listType ? noTopBorderCss : undefined,
          useFlexLayout ? gridContainerCss : gridAutoHeightCss,
        ]}
      >
        {pagination ? (
          <>
            <div css={gridFillRemainingCss}>{gridElement}</div>
            <SourcingGridPaginationFooter
              totalRowCount={paginationState.totalRowCount}
              currentPage={paginationState.currentPage}
              totalPages={paginationState.totalPages}
              pageSize={paginationState.pageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </>
        ) : (
          gridElement
        )}
      </div>
    </>
  );
}
